import React, { Suspense, lazy } from 'react';

import cashierIcon from '@ezugi/static/icons/cashier.svg';
import chatIcon from '@ezugi/static/icons/chat-mobile.svg';
import historyIcon from '@ezugi/static/icons/history.svg';
import languagesIcon from '@ezugi/static/icons/language.svg';
import payoutsIcon from '@ezugi/static/icons/payouts.svg';
import statisticsIcon from '@ezugi/static/icons/statistics.svg';
import supportIcon from '@ezugi/static/icons/support.svg';
import { DeviceInfo, isEvoWrapped } from '@ezugi/utils';

import circleIcon from '../../assets/icons/circle.svg';
import evoIcon from '../../assets/icons/evo.svg';
import homeIcon from '../../assets/icons/home.svg';
import infoIcon from '../../assets/icons/info.svg';
import moreGamesIcon from '../../assets/icons/more-games.svg';
import settingsIcon from '../../assets/icons/settings.svg';
import tipsIcon from '../../assets/icons/tips.svg';

const LanguageSelector = lazy(() => import('../../components/LanguageSelector'));

export const ADD_TO_FAVORITES_KEY = 'addToFavorites';
export const REMOVE_FROM_FAVORITES_KEY = 'removeFromFavorites';
export const TOGGLE_FAVORITE_KEY = 'toggleFavorite';
export const TIPS_KEY = 'tips';

export const linksMap = {
  minilobby: isEvoWrapped() ? {
    key: 'minilobby',
    'data-e2e': 'menu_link_minilobby',
    icon: evoIcon,
    onClick: () => {},
    drawer: true,
    color: '#96FFFF',
  } : {
    key: 'minilobby',
    'data-e2e': 'menu_link_minilobby',
    icon: moreGamesIcon,
    onClick: () => {},
    drawer: true,
  },
  toggleFavorite: {
    key: TOGGLE_FAVORITE_KEY,
    'data-e2e': 'menu_link_toggle_favorite',
    icon: null,
    onClick: () => {},
    drawer: false,
    keepMenu: true,
  },
  home: { key: 'home', 'data-e2e': 'menu_link_home', icon: homeIcon, onClick: () => {} },
  chat: {
    key: 'chat',
    'data-e2e': 'menu_link_chat',
    icon: chatIcon,
    drawer: true,
  },
  cashier: {
    key: 'cashier',
    'data-e2e': 'menu_link_cashier',
    icon: { svg: cashierIcon },
    onClick: () => {},
  },
  tips: { key: 'tips',
    'data-e2e': 'menu_link_tips',
    icon: tipsIcon,
    onClick: () => {},
    drawer: !DeviceInfo.isHandheld },
  stats: {
    key: 'stats',
    'data-e2e': 'menu_link_stats',
    icon: statisticsIcon,
    onClick: () => {},
    drawer: true,
    drawerBackIcon: true,
  },
  payouts: {
    key: 'payouts',
    'data-e2e': 'menu_link_payouts',
    icon: payoutsIcon,
    onClick: () => {},
    drawer: true,
  },
  playerHistory: {
    key: 'playerHistory',
    'data-e2e': 'menu_link_player_history',
    icon: historyIcon,
    onClick: () => {},
    drawer: true,
  },
  settings: {
    key: 'settings',
    'data-e2e': 'menu_link_settings',
    icon: settingsIcon,
    onClick: () => {},
    drawer: true,
  },
  gameInfo: {
    key: 'gameInfo',
    'data-e2e': 'menu_link_game_info',
    icon: infoIcon,
    onClick: () => {},
    drawer: true,
  },
  liveSupport: {
    key: 'liveSupport',
    'data-e2e': 'menu_link_live_support',
    icon: supportIcon,
    onClick: () => {},
    drawer: true,
  },
  languages: {
    key: 'languages',
    icon: languagesIcon,
    onClick: () => {},
    drawer: false,
    render: (
      <Suspense
        fallback={null}
        key="languageSelectorWrapper"
      >
        <LanguageSelector
          key="languageSelector"
        />
      </Suspense>
    ),
  },
  responsibleGaming: {
    key: 'responsibleGaming',
    'data-e2e': 'menu_link_responsible_gaming',
    icon: circleIcon,
    onClick: () => {},
    drawer: false,
  },
} as const;

export const localeKeys = {
  minilobby: isEvoWrapped() ? 'games.lobby' : 'general.more_games',
  welcome: 'general.welcome',
  home: 'general.home',
  chat: 'games_ui.chat',
  cashier: 'general.cashier',
  stats: 'general.statistics',
  tips: 'games_ui.tips',
  payouts: 'games_ui.limits_and_payouts',
  playerHistory: 'general.player_history',
  settings: 'games_ui.settings',
  gameInfo: 'general.game_info',
  liveSupport: 'games_ui.live_support',
  responsibleGaming: 'games_ui.responsible_gaming',
  [ADD_TO_FAVORITES_KEY]: 'general.add_to_favorites',
  [REMOVE_FROM_FAVORITES_KEY]: 'general.remove_from_favorites',
};

export const actionNames = {
  cashier: 'cashierRequest',
  home: 'homeRequest',
  tips: 'tipsRequest',
  responsibleGaming: 'responsibleGamingRequest',
  liveSupport: 'readSupportMessages',
  chat: 'readRoomMessages',
  minilobby: isEvoWrapped() ? 'homeRequest' : 'openMinilobbyWidget',
  gameInfo: 'sendGameInfoAnalytics',
  toggleFavorite: 'toggleFavorite',
  payouts: 'sendLimitsAndPayoutsAnalytics',
  playerHistory: 'sendPlayerHistoryAnalytics',
};

export const desktopLinks = [
  'minilobby',
  !isEvoWrapped() && 'toggleFavorite',
  'payouts',
  'playerHistory',
  'gameInfo',
  'settings',
  'tips',
  'languages',
  'home',
];

export const mobileLinks = [
  'minilobby',
  !isEvoWrapped() && 'toggleFavorite',
  'payouts',
  'playerHistory',
  'cashier',
  'gameInfo',
  'settings',
  'liveSupport',
  'chat',
  'tips',
  'languages',
  'stats',
  'responsibleGaming',
  'home',
];
